import React, { useState, useEffect } from 'react';
import {
  Pane, Text, Badge, IconButton,
  EditIcon, TrashIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon,
  CameraIcon, AnnotationIcon
} from 'evergreen-ui';

const STATUS_COLORS = {
  not_tested: '#8F95B2',
  in_progress: '#3366FF',
  tested: '#52BD94',
  vulnerable: '#D14343',
};

const STATUS_LABELS = {
  not_tested: 'Not Tested',
  in_progress: 'In Progress',
  tested: 'Tested',
  vulnerable: 'Vulnerable',
};

const STATUS_BG = {
  not_tested: '#F4F5F9',
  in_progress: '#F3F6FF',
  tested: '#F1FAF5',
  vulnerable: '#FDF4F4',
};

const METHOD_COLORS = {
  GET: '#61affe',
  POST: '#49cc90',
  PUT: '#fca130',
  DELETE: '#f93e3e',
  PATCH: '#50e3c2',
  HEAD: '#9012fe',
  OPTIONS: '#0d5aa7',
};

const TECHNIQUE_COLORS = {
  'XSS': '#E53E3E',
  'Blind XSS': '#E53E3E',
  'SQL Injection': '#DD6B20',
  'Blind SQLi': '#DD6B20',
  'SSRF': '#805AD5',
  'Command Injection': '#C53030',
  'Path Traversal': '#D69E2E',
  'SSTI': '#B83280',
  'XXE': '#2B6CB0',
  'Open Redirect': '#38A169',
  'File Upload': '#3182CE',
  'Auth Bypass': '#E53E3E',
  'IDOR': '#DD6B20',
  'CSRF': '#805AD5',
};

function getTechniqueColor(technique) {
  return TECHNIQUE_COLORS[technique] || '#718096';
}

const CARD_WIDTH = 230;
const CONNECTOR_LENGTH = 36;
const BRANCH_GAP = 22;

/* ─── Helpers ─── */

function buildTree(nodes) {
  const map = {};
  const roots = [];
  const sorted = [...nodes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  sorted.forEach(n => { map[n.id] = { ...n, children: [] }; });
  sorted.forEach(n => {
    if (n.parent_id && map[n.parent_id]) {
      map[n.parent_id].children.push(map[n.id]);
    } else {
      roots.push(map[n.id]);
    }
  });
  return roots;
}

function getCoverageStats(node) {
  let total = 0, tested = 0, vulnerable = 0;
  function walk(n) {
    total++;
    if (n.status === 'tested') tested++;
    if (n.status === 'vulnerable') { tested++; vulnerable++; }
    if (n.children) n.children.forEach(walk);
  }
  walk(node);
  return { total, tested, vulnerable, pct: total > 0 ? Math.round((tested / total) * 100) : 0 };
}

function highlightMatch(text, query) {
  if (!query || !query.trim()) return text;
  const q = query.trim();
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.substring(0, idx)}
      <span style={{ backgroundColor: '#FFF3CD', borderRadius: 2, padding: '0 2px', fontWeight: 600 }}>
        {text.substring(idx, idx + q.length)}
      </span>
      {text.substring(idx + q.length)}
    </>
  );
}

/* ─── Connector pieces ─── */

const HLine = ({ width, color = '#D8DAE5' }) => (
  <Pane width={width} height={2} flexShrink={0} style={{ background: color }} />
);

/* ─── Node Card ─── */

const NodeCard = ({
  node, depth, isSelected, hovered, hasChildren, expanded, stats,
  searchQuery, screenshotCount, hasNotes, onToggle
}) => {
  const statusColor = STATUS_COLORS[node.status] || '#8F95B2';
  const childCount = hasChildren ? node.children.length : 0;

  return (
    <Pane
      minWidth={CARD_WIDTH}
      maxWidth={320}
      minHeight={72}
      background="white"
      borderRadius={12}
      overflow="hidden"
      flexShrink={0}
      border={`1.5px solid ${isSelected ? '#3366FF' : hovered ? '#C1C4D6' : '#E6E8F0'}`}
      style={{
        boxShadow: isSelected
          ? '0 0 0 3px rgba(51,102,255,0.12), 0 4px 12px rgba(51,102,255,0.1)'
          : hovered
            ? '0 4px 12px rgba(67,90,111,0.1)'
            : '0 1px 4px rgba(67,90,111,0.06)',
        transition: 'all 0.18s ease',
      }}
    >
      {/* Status accent bar */}
      <Pane height={4} style={{ background: `linear-gradient(90deg, ${statusColor}, ${statusColor}88)` }} />

      <Pane padding={12}>
        {/* Method + Name */}
        <Pane display="flex" alignItems="center" gap={6} marginBottom={6}>
          {node.http_method && (
            <Pane
              paddingX={5}
              paddingY={1}
              borderRadius={3}
              flexShrink={0}
              style={{
                backgroundColor: METHOD_COLORS[node.http_method] || '#888',
                fontSize: 9,
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '0.5px',
                fontFamily: 'monospace',
                lineHeight: '14px',
              }}
            >
              {node.http_method}
            </Pane>
          )}
          <Text
            fontWeight={600}
            size={depth === 0 ? 400 : 300}
            color="#101840"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
            title={node.name}
          >
            {highlightMatch(node.name, searchQuery)}
          </Text>
        </Pane>

        {/* URL */}
        {node.url && (
          <Text
            size={200}
            fontFamily="mono"
            color="#8F95B2"
            display="block"
            marginBottom={6}
            style={{
              wordBreak: 'break-all',
              lineHeight: '1.4',
            }}
            title={node.url}
          >
            {highlightMatch(node.url, searchQuery)}
          </Text>
        )}

        {/* Technique and param badges from JSON description */}
        {node.description && (() => {
          try {
            const data = JSON.parse(node.description);
            const techniques = data.techniques || [];
            const params = data.params || [];
            if (techniques.length === 0 && params.length === 0) return null;
            return (
              <Pane display="flex" gap={3} flexWrap="wrap" marginBottom={6}>
                {techniques.slice(0, 3).map(tech => (
                  <Pane
                    key={tech}
                    paddingX={5}
                    paddingY={1}
                    borderRadius={3}
                    style={{
                      backgroundColor: getTechniqueColor(tech) + '18',
                      border: `1px solid ${getTechniqueColor(tech)}40`,
                      fontSize: 9,
                      fontWeight: 600,
                      color: getTechniqueColor(tech),
                      lineHeight: '14px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tech}
                  </Pane>
                ))}
                {params.slice(0, 3).map((p, i) => (
                  <Pane
                    key={`p-${i}`}
                    paddingX={5}
                    paddingY={1}
                    borderRadius={3}
                    style={{
                      backgroundColor: p.type === 'numeric' ? '#FFF8EC' : '#F4F5F9',
                      border: `1px solid ${p.type === 'numeric' ? '#DD6B2040' : '#E6E8F0'}`,
                      fontSize: 9,
                      fontWeight: 600,
                      color: p.type === 'numeric' ? '#DD6B20' : '#696F8C',
                      lineHeight: '14px',
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.in === 'path' ? `{${p.name}}` : `?${p.name}`}
                  </Pane>
                ))}
                {(techniques.length + params.length) > 3 && (
                  <Text size={200} color="#8F95B2" style={{ fontSize: 9, lineHeight: '16px' }}>
                    +{techniques.length + params.length - 3}
                  </Text>
                )}
              </Pane>
            );
          } catch { return null; }
        })()}

        {/* Status + metadata row */}
        <Pane display="flex" alignItems="center" gap={6} flexWrap="wrap">
          {/* Status pill */}
          <Pane
            display="inline-flex"
            alignItems="center"
            gap={4}
            paddingX={7}
            paddingY={2}
            borderRadius={10}
            background={STATUS_BG[node.status] || '#F4F5F9'}
          >
            <Pane
              width={6}
              height={6}
              borderRadius="50%"
              backgroundColor={statusColor}
              style={{
                boxShadow: node.status === 'vulnerable'
                  ? '0 0 5px rgba(209,67,67,0.5)'
                  : 'none',
              }}
            />
            <Text size={200} fontWeight={500} color={statusColor}>
              {STATUS_LABELS[node.status]}
            </Text>
          </Pane>

          <Pane flex={1} />

          {/* Icons: notes, screenshots */}
          {hasNotes && (
            <AnnotationIcon size={12} color="#8F95B2" style={{ opacity: 0.6 }} />
          )}
          {screenshotCount > 0 && (
            <Pane display="flex" alignItems="center" gap={2} style={{ opacity: 0.6 }}>
              <CameraIcon size={12} color="#8F95B2" />
              <Text size={200} color="#8F95B2">{screenshotCount}</Text>
            </Pane>
          )}
          {node.finding_count > 0 && (
            <Badge color="orange" style={{ fontSize: 9, padding: '0 5px' }}>
              {node.finding_count}
            </Badge>
          )}
        </Pane>

        {/* Progress bar for parent nodes */}
        {hasChildren && stats && (
          <Pane marginTop={8}>
            <Pane display="flex" alignItems="center" gap={6} marginBottom={4}>
              <Pane flex={1} height={4} borderRadius={2} background="#E6E8F0" overflow="hidden">
                <Pane
                  height="100%"
                  borderRadius={2}
                  width={`${stats.pct}%`}
                  style={{
                    background: stats.pct >= 80
                      ? 'linear-gradient(90deg, #52BD94, #429777)'
                      : stats.pct >= 40
                        ? 'linear-gradient(90deg, #FFB020, #E6991A)'
                        : stats.pct > 0
                          ? 'linear-gradient(90deg, #D14343, #B83535)'
                          : '#E6E8F0',
                    transition: 'width 0.4s ease',
                  }}
                />
              </Pane>
              <Text size={200} fontWeight={600} fontFamily="mono" color={
                stats.pct >= 80 ? '#52BD94' : stats.pct >= 40 ? '#FFB020' : '#D14343'
              }>
                {stats.pct}%
              </Text>
            </Pane>

            {/* Expand/Collapse toggle */}
            <Pane
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={4}
              paddingY={3}
              marginTop={4}
              borderRadius={6}
              cursor="pointer"
              background={expanded ? '#F3F6FF' : '#F4F5F9'}
              border={`1px solid ${expanded ? '#D6E0FF' : '#E6E8F0'}`}
              onClick={e => { e.stopPropagation(); onToggle(); }}
              style={{ transition: 'all 0.15s ease' }}
            >
              <Text size={200} fontWeight={500} color={expanded ? '#3366FF' : '#696F8C'}>
                {expanded ? 'Collapse' : `Expand ${childCount} node${childCount !== 1 ? 's' : ''}`}
              </Text>
              <Text size={200} color={expanded ? '#3366FF' : '#696F8C'}>
                {expanded ? '−' : '+'}
              </Text>
            </Pane>
          </Pane>
        )}
      </Pane>
    </Pane>
  );
};

/* ─── Mind Map Node (card + children branch) ─── */

const MindMapNode = ({
  node, depth, index, siblingCount, onAdd, onEdit, onDelete,
  onReorder, onSelect, selectedNodeId, canWrite, expandAll, searchQuery
}) => {
  const [expanded, setExpanded] = useState(true);
  const [hovered, setHovered] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const stats = hasChildren ? getCoverageStats(node) : null;
  const isSelected = selectedNodeId === node.id;
  const screenshotCount = (node.screenshots || []).length;
  const hasNotes = !!(node.notes && node.notes.trim());

  useEffect(() => { setExpanded(expandAll); }, [expandAll]);

  const statusColor = STATUS_COLORS[node.status] || '#8F95B2';
  const connectorColor = `${statusColor}50`;

  return (
    <Pane display="flex" alignItems="center">
      {/* Card + hover actions */}
      <Pane
        position="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onSelect && onSelect(node)}
        cursor="pointer"
      >
        <NodeCard
          node={node}
          depth={depth}
          isSelected={isSelected}
          hovered={hovered}
          hasChildren={hasChildren}
          expanded={expanded}
          stats={stats}
          searchQuery={searchQuery}
          screenshotCount={screenshotCount}
          hasNotes={hasNotes}
          onToggle={() => setExpanded(!expanded)}
        />

        {/* Floating action bar */}
        {canWrite && (hovered || isSelected) && (
          <Pane
            position="absolute"
            bottom={-28}
            left="50%"
            display="flex"
            gap={2}
            padding={3}
            background="white"
            borderRadius={8}
            border="1px solid #E6E8F0"
            onClick={e => e.stopPropagation()}
            style={{
              transform: 'translateX(-50%)',
              boxShadow: '0 2px 8px rgba(67,90,111,0.12)',
              zIndex: 10,
              opacity: hovered ? 1 : 0.7,
              transition: 'opacity 0.15s ease',
            }}
          >
            <IconButton icon={PlusIcon} appearance="minimal" size="small" onClick={() => onAdd(node.id)} title="Add child" />
            <IconButton icon={EditIcon} appearance="minimal" size="small" onClick={() => onEdit(node)} title="Edit" />
            <IconButton icon={TrashIcon} appearance="minimal" size="small" intent="danger" onClick={() => onDelete(node)} title="Delete" />
            {index > 0 && (
              <IconButton icon={ArrowUpIcon} appearance="minimal" size="small" onClick={() => onReorder(node.id, 'up')} title="Move up" />
            )}
            {index < siblingCount - 1 && (
              <IconButton icon={ArrowDownIcon} appearance="minimal" size="small" onClick={() => onReorder(node.id, 'down')} title="Move down" />
            )}
          </Pane>
        )}
      </Pane>

      {/* Children branch */}
      {hasChildren && expanded && (
        <Pane display="flex" alignItems="center">
          {/* Horizontal connector from card to branch point */}
          <HLine width={CONNECTOR_LENGTH} color={connectorColor} />

          {/* Children column */}
          <Pane display="flex" flexDirection="column">
            {node.children.map((child, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === node.children.length - 1;
              const isOnly = node.children.length === 1;

              return (
                <Pane
                  key={child.id}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  paddingY={BRANCH_GAP / 2}
                >
                  {/* Vertical line segments (each child draws its own half-segments) */}
                  {!isOnly && (
                    <>
                      {/* Top half - present for all except first */}
                      {!isFirst && (
                        <Pane
                          position="absolute"
                          left={0}
                          top={0}
                          width={2}
                          style={{ bottom: '50%', background: connectorColor }}
                        />
                      )}
                      {/* Bottom half - present for all except last */}
                      {!isLast && (
                        <Pane
                          position="absolute"
                          left={0}
                          bottom={0}
                          width={2}
                          style={{ top: '50%', background: connectorColor }}
                        />
                      )}
                    </>
                  )}

                  {/* Horizontal branch to child */}
                  <HLine width={BRANCH_GAP} color={connectorColor} />

                  {/* Recursive child */}
                  <MindMapNode
                    node={child}
                    depth={depth + 1}
                    index={idx}
                    siblingCount={node.children.length}
                    onAdd={onAdd}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onReorder={onReorder}
                    onSelect={onSelect}
                    selectedNodeId={selectedNodeId}
                    canWrite={canWrite}
                    expandAll={expandAll}
                    searchQuery={searchQuery}
                  />
                </Pane>
              );
            })}
          </Pane>
        </Pane>
      )}
    </Pane>
  );
};

/* ─── Root component ─── */

const CoverageTree = ({
  nodes, onAdd, onEdit, onDelete, onReorder, onSelect,
  selectedNodeId, canWrite, expandAll, searchQuery
}) => {
  const tree = buildTree(nodes);

  return (
    <Pane display="flex" flexDirection="column" gap={24} padding={8}>
      {tree.map((node, idx) => (
        <MindMapNode
          key={node.id}
          node={node}
          depth={0}
          index={idx}
          siblingCount={tree.length}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onReorder={onReorder}
          onSelect={onSelect}
          selectedNodeId={selectedNodeId}
          canWrite={canWrite}
          expandAll={expandAll}
          searchQuery={searchQuery}
        />
      ))}
    </Pane>
  );
};

export default CoverageTree;
